import React, { Component } from 'react';
import { renderCoverBlob } from '../utils/imageResize';
import { getPortadaState, uploadPortada } from '../utils/imageApi';
import {
    PORTADA_SIZES,
    PORTADA_SLIDES,
    portadaUrl,
    formatoPeso,
    bucketDePortada,
    ANCHO_RECOMENDADO,
    ALTO_RECOMENDADO,
    ANCHO_MINIMO,
    ALTO_MINIMO,
    PESO_MAXIMO,
} from '../utils/portada';

/**
 * Pantalla de las imágenes del carrusel del home del sitio público.
 *
 * El admin sube UNA imagen grande por slide y elige el punto focal; de ahí salen las cuatro
 * medidas que pide el frontend (celular, tablet, notebook, monitor), recortadas con la misma
 * regla que usa el navegador para mostrarlas (`object-fit: cover`) y comprimidas a WebP con
 * el mismo util que optimiza los thumbnails de producto.
 *
 * Por qué así y no cuatro archivos a mano (que es lo que había antes): las cuatro cajas del
 * hero tienen relaciones de aspecto muy distintas, y pedirle al admin que prepare cuatro
 * recortes consistentes es exactamente lo que falló — en producción quedaron dos imágenes
 * verticales, una de 12 MB en PNG con nombre .webp, y dos de los tres slides sin imagen.
 */

/**
 * Recorte 1 (el nuestro): qué parte del original entra en el archivo, según el punto focal.
 * En porcentajes del original.
 */
const regionVisible = (origen, destino, focusX, focusY) => {
    const escala = Math.max(destino.width / origen.width, destino.height / origen.height);
    const visW = Math.min(origen.width, destino.width / escala);
    const visH = Math.min(origen.height, destino.height / escala);
    return {
        left: ((origen.width - visW) * focusX) / origen.width,
        top: ((origen.height - visH) * focusY) / origen.height,
        width: visW / origen.width,
        height: visH / origen.height,
    };
};

/**
 * Recorte 1 + recorte 2: lo que realmente se ve en una pantalla.
 *
 * El segundo lo hace el navegador al meter el archivo en la caja del hero (`object-cover
 * object-center`), y es CENTRADO: no respeta el punto focal. Por eso no alcanza con simular
 * el archivo — si lo hiciéramos, el preview prometería más de lo que el sitio muestra.
 */
const regionEnPantalla = (origen, size, cajaRatio, focusX, focusY) => {
    const r = regionVisible(origen, size, focusX, focusY);
    const archivoRatio = size.width / size.height;
    // Caja más ancha que el archivo → se recorta alto; más angosta → se recorta ancho.
    const fracW = cajaRatio >= archivoRatio ? 1 : cajaRatio / archivoRatio;
    const fracH = cajaRatio >= archivoRatio ? archivoRatio / cajaRatio : 1;
    return {
        left: r.left + (r.width * (1 - fracW)) / 2,
        top: r.top + (r.height * (1 - fracH)) / 2,
        width: r.width * fracW,
        height: r.height * fracH,
    };
};

/**
 * Lo que sobrevive en TODAS las pantallas a la vez. Se evalúan las dos formas extremas de la
 * caja en cada tramo (`cajaMin`/`cajaMax`), no la del archivo: dentro de un mismo tramo la
 * caja cambia de forma con el ancho de la ventana, y el recorte de más llega al 25%.
 */
const zonaSegura = (origen, focusX, focusY) => {
    const regiones = [];
    PORTADA_SIZES.forEach((s) => {
        regiones.push(regionEnPantalla(origen, s, s.cajaMin, focusX, focusY));
        regiones.push(regionEnPantalla(origen, s, s.cajaMax, focusX, focusY));
    });
    const left = Math.max(...regiones.map((r) => r.left));
    const top = Math.max(...regiones.map((r) => r.top));
    const right = Math.min(...regiones.map((r) => r.left + r.width));
    const bottom = Math.min(...regiones.map((r) => r.top + r.height));
    return { left, top, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
};

const acotar = (v) => Math.min(1, Math.max(0, v));

class PortadaImagesComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // key del slide -> timestamp de su última subida, para saltear el caché del navegador
            // en los previews. Por slide y no global: si no, subir uno vuelve a bajar los tres.
            bust: {},
            // key del slide -> { file, url, width, height, focusX, focusY, aviso }
            seleccion: {},
            // key del slide -> { sizeKey -> { estado: 'ok'|'falta', width, height } }
            actuales: {},
            subiendo: null,
            progreso: 0,
            mensaje: null,
        };
        this.inputs = {};
    }

    componentDidMount() {
        // Una sola llamada trae los 12 objetos; antes era un getMetadata por objeto.
        this.cargarPesos();
    }

    componentWillUnmount() {
        this.desmontado = true;
        Object.values(this.state.seleccion).forEach((s) => s && s.url && URL.revokeObjectURL(s.url));
    }

    // --- estado de lo que ya está en el bucket -------------------------------------------------

    /** Merge por (slide, medida): las dimensiones y el peso llegan por caminos distintos. */
    registrarActual = (slideKey, sizeKey, datos) => {
        if (this.desmontado) return;
        this.setState((prev) => ({
            actuales: {
                ...prev.actuales,
                [slideKey]: {
                    ...(prev.actuales[slideKey] || {}),
                    [sizeKey]: { ...((prev.actuales[slideKey] || {})[sizeKey] || {}), ...datos },
                },
            },
        }));
    };

    /** Las dimensiones las reporta el propio <img>; el peso no se puede sacar de ahí. */
    registrarDimensiones = (slideKey, sizeKey, estado, img) =>
        this.registrarActual(slideKey, sizeKey, {
            estado,
            width: img ? img.naturalWidth : null,
            height: img ? img.naturalHeight : null,
        });

    /**
     * El peso importa tanto como el recorte: el caso que motivó esta pantalla fue un PNG de
     * 12,3 MB con nombre .webp servido justo en el tramo de celulares y tablets. `getMetadata`
     * lo dice sin bajar el archivo.
     */
    cargarPesos = (slide) => {
        // ENI-167: el backend devuelve el estado de los 12 objetos (4 tamaños × 3 slides) de una
        // sola vez, así que la llamada ya no es por objeto. Se filtra por slide para no cambiar
        // la firma ni los dos lugares que la invocan (montaje y post-subida).
        return getPortadaState()
            .then((estado) => {
                if (this.desmontado) return;
                estado
                    .filter((item) => (slide ? item.slide === slide.file : true))
                    .forEach((item) => {
                        // No existe: el <img> ya reporta "falta", no hay peso que mostrar.
                        if (!item.exists) return;
                        const conocido = PORTADA_SLIDES.find((s) => s.file === item.slide);
                        if (!conocido) return;
                        this.registrarActual(conocido.key, item.size, {
                            peso: item.size_bytes,
                            tipo: item.contentType,
                        });
                    });
            })
            .catch(() => {});
    };

    // --- selección de archivo ------------------------------------------------------------------

    elegirArchivo = (slideKey) => {
        const input = this.inputs[slideKey];
        if (input) input.click();
    };

    onArchivoElegido = async (event, slideKey) => {
        const file = event.target.files && event.target.files[0];
        // Permitir cancelar el diálogo sin romper nada (el componente anterior explotaba acá).
        event.target.value = '';
        if (!file) return;

        if (!file.type || !file.type.startsWith('image/')) {
            this.setState({ mensaje: { type: 'danger', text: 'El archivo no es una imagen.' } });
            return;
        }
        if (file.type === 'image/svg+xml') {
            this.setState({ mensaje: { type: 'danger', text: 'Los SVG no sirven para la portada: subí una foto (JPG, PNG o WebP).' } });
            return;
        }

        let dimensiones;
        try {
            dimensiones = await this.medirImagen(file);
        } catch (e) {
            this.setState({ mensaje: { type: 'danger', text: 'No se pudo leer la imagen. Probá con otro archivo.' } });
            return;
        }

        // Se miran las DOS dimensiones: recortar "cover" a WxH necesita ancho >= W y alto >= H.
        // Con solo el ancho, una panorámica de 3000x400 pasaba sin un solo aviso y salía más
        // chica en las cuatro medidas.
        if (dimensiones.width < ANCHO_MINIMO || dimensiones.height < ALTO_MINIMO) {
            this.setState({
                mensaje: {
                    type: 'danger',
                    text: `"${file.name}" mide ${dimensiones.width}x${dimensiones.height}px y el mínimo es ${ANCHO_MINIMO}x${ALTO_MINIMO}px (lo que necesita la medida de celular). Más chica que eso se vería borrosa hasta en un teléfono.`,
                },
            });
            return;
        }

        const aviso = (dimensiones.width < ANCHO_RECOMENDADO || dimensiones.height < ALTO_RECOMENDADO)
            ? `La imagen mide ${dimensiones.width}x${dimensiones.height}px y para llenar las cuatro medidas hacen falta ${ANCHO_RECOMENDADO}x${ALTO_RECOMENDADO}px. Se genera igual, pero las más grandes van a salir en la resolución que soporte el original.`
            : null;

        this.setState((prev) => {
            const anterior = prev.seleccion[slideKey];
            if (anterior && anterior.url) URL.revokeObjectURL(anterior.url);
            return {
                mensaje: null,
                seleccion: {
                    ...prev.seleccion,
                    [slideKey]: {
                        file,
                        url: URL.createObjectURL(file),
                        width: dimensiones.width,
                        height: dimensiones.height,
                        focusX: 0.5,
                        focusY: 0.5,
                        aviso,
                    },
                },
            };
        });
    };

    medirImagen = (file) =>
        new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(url);
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            };
            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('imagen ilegible'));
            };
            img.src = url;
        });

    cancelarSeleccion = (slideKey) => {
        this.setState((prev) => {
            const sel = prev.seleccion[slideKey];
            if (sel && sel.url) URL.revokeObjectURL(sel.url);
            const seleccion = { ...prev.seleccion };
            delete seleccion[slideKey];
            return { seleccion, mensaje: null };
        });
    };

    // --- punto focal ---------------------------------------------------------------------------

    /** El foco se congela mientras se sube: la subida ya capturó el que había al apretar. */
    focoEditable = (slideKey) => !this.state.subiendo && !!this.state.seleccion[slideKey];

    moverFoco = (event, slideKey) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const focusX = acotar((event.clientX - rect.left) / rect.width);
        const focusY = acotar((event.clientY - rect.top) / rect.height);
        this.setState((prev) => ({
            seleccion: { ...prev.seleccion, [slideKey]: { ...prev.seleccion[slideKey], focusX, focusY } },
        }));
    };

    onFocoDown = (event, slideKey) => {
        // Solo botón izquierdo y puntero primario: con el derecho (menú contextual) el foco
        // saltaba al cursor, y encima el `pointerup` no llega mientras el menú está abierto,
        // así que quedaba "arrastrando" y después seguía al mouse sin apretar nada.
        if (event.button !== 0 || event.isPrimary === false) return;
        if (!this.focoEditable(slideKey)) return;
        this.arrastrando = slideKey;
        if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        this.moverFoco(event, slideKey);
    };

    onFocoMove = (event, slideKey) => {
        if (this.arrastrando !== slideKey) return;
        if (!this.focoEditable(slideKey)) { this.arrastrando = null; return; }
        this.moverFoco(event, slideKey);
    };

    onFocoUp = () => {
        this.arrastrando = null;
    };

    centrarFoco = (slideKey) => {
        if (!this.focoEditable(slideKey)) return;
        this.setState((prev) => ({
            seleccion: { ...prev.seleccion, [slideKey]: { ...prev.seleccion[slideKey], focusX: 0.5, focusY: 0.5 } },
        }));
    };

    // --- subida --------------------------------------------------------------------------------

    subir = async (slide) => {
        const sel = this.state.seleccion[slide.key];
        if (!sel || this.state.subiendo) return;

        this.setState({ subiendo: slide.key, progreso: 0, mensaje: null });

        const subidas = [];
        const limitadas = [];
        let sinWebp = false;
        try {
            for (let i = 0; i < PORTADA_SIZES.length; i++) {
                const size = PORTADA_SIZES[i];
                const generado = await renderCoverBlob(sel.file, {
                    width: size.width,
                    height: size.height,
                    focusX: sel.focusX,
                    focusY: sel.focusY,
                });

                // ENI-167: va por el backend. El `cacheControl: 'public, max-age=3600'` que antes
                // se mandaba acá ahora lo pone el servidor (es propio del destino "portada", que
                // se pisa siempre con el mismo nombre). Sin progreso parcial por archivo: `fetch`
                // no lo expone, y son 4 subidas, así que la barra avanza de a una.
                await uploadPortada(size.key, slide.file, generado.blob);
                // Si el componente se desmontó (el admin navegó a otra pantalla) NO se corta el
                // loop: hay que terminar las 4 medidas o el home queda con la portada nueva en
                // celular y la vieja en monitor. Sólo se evita el setState sobre un componente
                // muerto.
                if (!this.desmontado) {
                    this.setState({ progreso: ((i + 1) / PORTADA_SIZES.length) * 100 });
                }

                subidas.push(`${size.label} ${generado.width}x${generado.height} (${formatoPeso(generado.blob.size)})`);
                if (generado.limitadoPorElOriginal) limitadas.push(size.label);
                if (generado.blob.type !== 'image/webp') sinWebp = true;
            }

            if (sel.url) URL.revokeObjectURL(sel.url);
            if (this.desmontado) return;

            const avisos = [];
            if (limitadas.length) {
                avisos.push(`${limitadas.join(' y ')} salieron más chicas que el objetivo porque el original no daba.`);
            }
            if (sinWebp) {
                avisos.push('Este navegador no genera WebP, así que se subió JPEG (funciona igual, pero pesa más: probá desde Chrome).');
            }

            this.setState(
                (prev) => {
                    const seleccion = { ...prev.seleccion };
                    delete seleccion[slide.key];
                    return {
                        seleccion,
                        subiendo: null,
                        progreso: 0,
                        // El `bust` es por slide: si fuera global, subir uno volvería a bajar las
                        // 12 imágenes de los tres.
                        bust: { ...prev.bust, [slide.key]: Date.now() },
                        // Se borra el estado viejo del slide: los <img> con el `bust` nuevo lo
                        // vuelven a reportar.
                        actuales: { ...prev.actuales, [slide.key]: {} },
                        mensaje: {
                            type: avisos.length ? 'warning' : 'success',
                            text: `"${slide.nombre}" actualizado: ${subidas.join(' · ')}. ${avisos.join(' ')} En el sitio puede tardar hasta una hora en verse, por el caché del navegador.`,
                        },
                    };
                },
                // Después del borrado, si no los pesos nuevos llegarían antes y se perderían.
                () => this.cargarPesos(slide)
            );
        } catch (e) {
            console.error('[portada] falló la subida', e);
            if (this.desmontado) return;
            // No decimos "error" a secas: si se cortó a la mitad quedan medidas nuevas y medidas
            // viejas conviviendo, y el admin tiene que saberlo para reintentar.
            const detalle = subidas.length
                ? ` Se alcanzaron a subir ${subidas.length} de ${PORTADA_SIZES.length} medidas: volvé a intentar para dejarlas todas iguales.`
                : '';
            this.setState((prev) => ({
                subiendo: null,
                progreso: 0,
                bust: { ...prev.bust, [slide.key]: Date.now() },
                mensaje: {
                    type: 'danger',
                    text: `No se pudo subir la imagen (${e && e.message ? e.message : 'error desconocido'}).${detalle}`,
                },
            }));
        }
    };

    // --- render --------------------------------------------------------------------------------

    renderEstadoActual(slide) {
        const estados = this.state.actuales[slide.key] || {};
        return (
            <table className="table table-sm mb-0" style={{ fontSize: '.8rem' }}>
                <tbody>
                    {PORTADA_SIZES.map((size) => {
                        const info = estados[size.key] || null;
                        const esperado = size.width / size.height;
                        const real = info && info.width ? info.width / info.height : null;
                        const recorteRaro = real !== null && Math.abs(real - esperado) / esperado > 0.1;
                        const pesado = info && info.peso > PESO_MAXIMO;
                        return (
                            <tr key={size.key}>
                                <td className="pl-0" style={{ whiteSpace: 'nowrap' }}>{size.label}</td>
                                <td className="text-muted">{size.width}x{size.height}</td>
                                <td className="text-right pr-0">
                                    {(!info || !info.estado) && <span className="text-muted">…</span>}
                                    {/* El <img> va por la URL pública y `getMetadata` por el SDK
                                        autenticado: si el objeto existe pero no se puede ver, el
                                        problema es el acceso del bucket, no que falte la imagen. */}
                                    {info && info.estado === 'falta' && info.peso == null && (
                                        <span className="badge badge-danger">falta</span>
                                    )}
                                    {info && info.estado === 'falta' && info.peso != null && (
                                        <span className="badge badge-warning" title="El archivo está en el bucket pero no se puede leer desde afuera: el sitio tampoco lo va a poder mostrar.">
                                            existe pero no es pública · {formatoPeso(info.peso)}
                                        </span>
                                    )}
                                    {info && info.estado === 'ok' && (
                                        <span>
                                            <span
                                                className={recorteRaro ? 'badge badge-warning' : 'text-success'}
                                                title={recorteRaro ? `El archivo mide ${info.width}x${info.height}: otra relación de aspecto, el hero lo recorta.` : ''}
                                            >
                                                {info.width}x{info.height}{recorteRaro ? ' · recorte incorrecto' : ''}
                                            </span>
                                            {info.peso != null && (
                                                <span
                                                    className={pesado ? 'badge badge-danger ml-1' : 'text-muted ml-1'}
                                                    title={pesado ? 'Muy pesada: la descarga cada visitante que entra al home.' : ''}
                                                >
                                                    {formatoPeso(info.peso)}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderPreviewSlide(slide, sel, size, ancho) {
        // La caja es la del DISPOSITIVO, no la del archivo: es lo que la persona va a ver.
        const alto = Math.round(ancho / size.muestra.ratio);
        const escalaTexto = alto / 200;
        // Y adentro, el archivo tapando esa caja como lo hace el navegador: centrado y con su
        // propia relación de aspecto. Así el preview incluye los DOS recortes.
        const escalaArchivo = Math.max(ancho / size.width, alto / size.height);
        const archivoW = size.width * escalaArchivo;
        const archivoH = size.height * escalaArchivo;
        return (
            <div key={size.key} className="mr-3 mb-3">
                <div className="text-muted mb-1" style={{ fontSize: '.75rem' }}>
                    {size.label} <span className="text-black-50">· {size.muestra.label}</span>
                </div>
                <div
                    style={{
                        position: 'relative',
                        width: ancho,
                        height: alto,
                        overflow: 'hidden',
                        borderRadius: 4,
                        background: '#e9ecef',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            width: archivoW,
                            height: archivoH,
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        <img
                            src={sel.url}
                            alt=""
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: `${sel.focusX * 100}% ${sel.focusY * 100}%`,
                                display: 'block',
                            }}
                        />
                    </div>
                    {/* El degradado y el texto que el sitio dibuja encima: si tapan el motivo, se ve acá. */}
                    <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, background: slide.overlay }} />
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: slide.align === 'right' ? 'flex-end' : 'flex-start',
                            textAlign: slide.align === 'right' ? 'right' : 'left',
                            padding: `0 ${Math.round(12 * escalaTexto)}px`,
                            color: '#fff',
                        }}
                    >
                        <div style={{ fontWeight: 700, lineHeight: 1.1, fontSize: Math.round(15 * escalaTexto), maxWidth: '60%' }}>
                            {slide.titulo}
                        </div>
                        <div style={{ opacity: .85, fontSize: Math.round(9 * escalaTexto), marginTop: 3 * escalaTexto, maxWidth: '60%' }}>
                            {slide.subtitulo}
                        </div>
                        <div
                            style={{
                                marginTop: 5 * escalaTexto,
                                background: '#2f6fed',
                                borderRadius: 3,
                                padding: `${Math.round(3 * escalaTexto)}px ${Math.round(7 * escalaTexto)}px`,
                                fontSize: Math.round(8 * escalaTexto),
                                fontWeight: 600,
                            }}
                        >
                            {slide.boton}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderEditor(slide, sel) {
        const origen = { width: sel.width, height: sel.height };
        const zona = zonaSegura(origen, sel.focusX, sel.focusY);
        const subiendoEste = this.state.subiendo === slide.key;

        return (
            <div className="border-top pt-3 mt-3">
                <div className="row">
                    <div className="col-lg-5 mb-3">
                        <label className="d-block mb-1"><strong>1. Elegí el punto importante de la foto</strong></label>
                        <p className="text-muted mb-2" style={{ fontSize: '.8rem' }}>
                            Hacé clic (o arrastrá) sobre lo que no se puede perder. El recuadro es lo que
                            se ve <strong>en todas las pantallas</strong>: afuera de ahí, algún tamaño lo corta.
                        </p>
                        <div
                            style={{
                                position: 'relative',
                                cursor: subiendoEste ? 'progress' : 'crosshair',
                                userSelect: 'none',
                                touchAction: 'none',
                                // Sin esto el `box-shadow` gigante que oscurece el resto de la
                                // foto se derrama sobre TODA la página.
                                overflow: 'hidden',
                                borderRadius: 4,
                                lineHeight: 0,
                            }}
                            onPointerDown={(e) => this.onFocoDown(e, slide.key)}
                            onPointerMove={(e) => this.onFocoMove(e, slide.key)}
                            onPointerUp={this.onFocoUp}
                            onPointerCancel={this.onFocoUp}
                        >
                            <img src={sel.url} alt="" style={{ width: '100%', display: 'block', borderRadius: 4 }} draggable={false} />
                            <div
                                style={{
                                    position: 'absolute',
                                    left: `${zona.left * 100}%`,
                                    top: `${zona.top * 100}%`,
                                    width: `${zona.width * 100}%`,
                                    height: `${zona.height * 100}%`,
                                    border: '2px solid rgba(255,255,255,.95)',
                                    boxShadow: '0 0 0 9999px rgba(0,0,0,.35)',
                                    pointerEvents: 'none',
                                }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    left: `${sel.focusX * 100}%`,
                                    top: `${sel.focusY * 100}%`,
                                    width: 16,
                                    height: 16,
                                    marginLeft: -8,
                                    marginTop: -8,
                                    borderRadius: '50%',
                                    border: '2px solid #fff',
                                    background: 'rgba(47,111,237,.9)',
                                    boxShadow: '0 0 0 1px rgba(0,0,0,.4)',
                                    pointerEvents: 'none',
                                }}
                            />
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                            <small className="text-muted">
                                Original {sel.width}x{sel.height} · {formatoPeso(sel.file.size)}
                            </small>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => this.centrarFoco(slide.key)}
                                disabled={!!this.state.subiendo}
                            >
                                Centrar
                            </button>
                        </div>
                        {sel.aviso && <div className="alert alert-warning py-2 mt-2 mb-0" style={{ fontSize: '.8rem' }}>{sel.aviso}</div>}
                    </div>

                    <div className="col-lg-7 mb-3">
                        <label className="d-block mb-1"><strong>2. Así queda en cada pantalla</strong></label>
                        <p className="text-muted mb-2" style={{ fontSize: '.8rem' }}>
                            El degradado y el texto los dibuja el sitio encima de la foto: no hace falta que
                            la imagen los traiga.
                        </p>
                        <div className="d-flex flex-wrap align-items-start">
                            {PORTADA_SIZES.map((size) => this.renderPreviewSlide(slide, sel, size, 215))}
                        </div>
                    </div>
                </div>

                {subiendoEste && (
                    <div className="progress mb-2" style={{ height: 6 }}>
                        <div className="progress-bar bg-success" role="progressbar" style={{ width: `${this.state.progreso}%` }} />
                    </div>
                )}

                <div className="text-right">
                    <button
                        type="button"
                        className="btn btn-outline-secondary mr-2"
                        onClick={() => this.cancelarSeleccion(slide.key)}
                        disabled={subiendoEste}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => this.subir(slide)}
                        disabled={!!this.state.subiendo}
                    >
                        {subiendoEste ? 'Subiendo…' : 'Guardar las 4 medidas'}
                    </button>
                </div>
            </div>
        );
    }

    renderSlide(slide) {
        const sel = this.state.seleccion[slide.key];
        const grande = PORTADA_SIZES[2]; // la de notebook: la más representativa para mirar
        const estadoGrande = (this.state.actuales[slide.key] || {})[grande.key];
        const faltaGrande = estadoGrande && estadoGrande.estado === 'falta';
        // "No hay imagen" solo si además la metadata dice que el objeto no existe; si existe y
        // no se ve, el cartel tiene que decir otra cosa (el sitio tampoco la va a mostrar).
        const existePeroNoSeVe = faltaGrande && estadoGrande.peso != null;
        return (
            <div className="card card-small mb-4" key={slide.key}>
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <div>
                        <h6 className="m-0">{slide.nombre}</h6>
                        <small className="text-muted">{slide.ayuda}</small>
                    </div>
                    <code className="text-muted" style={{ fontSize: '.75rem' }}>{slide.file}</code>
                </div>
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-7 mb-3">
                            <div style={{ position: 'relative', background: '#f5f6f8', borderRadius: 4, overflow: 'hidden', maxWidth: 460 }}>
                                <img
                                    src={portadaUrl(grande.key, slide.file, this.state.bust[slide.key])}
                                    alt=""
                                    style={{ width: '100%', display: faltaGrande ? 'none' : 'block' }}
                                    onLoad={(e) => this.registrarDimensiones(slide.key, grande.key, 'ok', e.target)}
                                    onError={() => this.registrarDimensiones(slide.key, grande.key, 'falta')}
                                />
                                {faltaGrande && (
                                    <div
                                        className="d-flex flex-column align-items-center justify-content-center text-muted"
                                        style={{ aspectRatio: `${grande.width} / ${grande.height}`, minHeight: 120, border: '1px dashed #ced4da', borderRadius: 4 }}
                                    >
                                        <i className="material-icons" style={{ fontSize: 32, opacity: .5 }}>image_not_supported</i>
                                        <small className="mt-1 text-center px-3">
                                            {existePeroNoSeVe
                                                ? 'La imagen está en el bucket pero no se puede leer desde afuera: en el sitio tampoco se ve.'
                                                : 'Este slide no tiene imagen: hoy se ve solo el degradado.'}
                                        </small>
                                    </div>
                                )}
                            </div>
                            {/* Los otros tres tamaños se cargan ocultos solo para reportar si existen. */}
                            <div style={{ display: 'none' }}>
                                {PORTADA_SIZES.filter((s) => s.key !== grande.key).map((size) => (
                                    <img
                                        key={size.key}
                                        src={portadaUrl(size.key, slide.file, this.state.bust[slide.key])}
                                        alt=""
                                        onLoad={(e) => this.registrarDimensiones(slide.key, size.key, 'ok', e.target)}
                                        onError={() => this.registrarDimensiones(slide.key, size.key, 'falta')}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="col-md-5 mb-3">
                            <label className="text-muted d-block mb-1" style={{ fontSize: '.75rem', textTransform: 'uppercase' }}>
                                Lo que hay hoy
                            </label>
                            {this.renderEstadoActual(slide)}
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm mt-3"
                                onClick={() => this.elegirArchivo(slide.key)}
                                disabled={!!this.state.subiendo}
                            >
                                {sel ? 'Elegir otra imagen…' : 'Cambiar imagen…'}
                            </button>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                ref={(el) => { this.inputs[slide.key] = el; }}
                                onChange={(e) => this.onArchivoElegido(e, slide.key)}
                            />
                        </div>
                    </div>
                    {sel && this.renderEditor(slide, sel)}
                </div>
            </div>
        );
    }

    render() {
        const { mensaje } = this.state;
        return (
            <div>
                {mensaje && <div className={`alert alert-${mensaje.type}`}>{mensaje.text}</div>}

                <div className="card card-small mb-4">
                    <div className="card-body py-3">
                        <p className="mb-1" style={{ fontSize: '.85rem' }}>
                            Son las tres imágenes del carrusel del home. Subí <strong>una sola foto grande</strong> por
                            slide (lo ideal, {ANCHO_RECOMENDADO}px de ancho o más) y elegí qué parte tiene que verse:
                            de ahí salen solas las cuatro medidas que usa el sitio, ya optimizadas.
                        </p>
                        <small className="text-muted">
                            Se guardan en <code>{bucketDePortada()}</code>. Ojo: cada ambiente escribe en su propio
                            bucket, así que para cambiar lo que se ve en la tienda hay que hacerlo desde el
                            backoffice de producción.
                        </small>
                    </div>
                </div>

                {PORTADA_SLIDES.map((slide) => this.renderSlide(slide))}
            </div>
        );
    }
}

export default PortadaImagesComponent;
